import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Search, Loader2, CheckCircle2, CalendarClock, SearchX } from "lucide-react";
import { api } from "@/lib/api";

const STATUS_STYLE = {
  completed: "text-[#3FBF7F] border-[#3FBF7F]/50 bg-[#3FBF7F]/10",
  scheduled: "text-[#F26A21] border-[#F26A21]/50 bg-[#F26A21]/10",
  upcoming: "text-[#AEBECD] border-[#33506e] bg-white/5",
};

export default function CustomerLookup() {
  const [params] = useSearchParams();
  const [id, setId] = useState(params.get("id") || "");
  const [loading, setLoading] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const runLookup = useCallback(async (rawId) => {
    const cid = (rawId || "").trim();
    if (!cid) {
      toast.error("Enter your Customer ID.");
      return;
    }
    setLoading(true);
    setNotFound(false);
    setCustomer(null);
    try {
      const { data } = await api.get(`/customers/lookup/${encodeURIComponent(cid)}`);
      setCustomer(data);
    } catch (err) {
      setNotFound(true);
      toast.error("No customer found with that ID.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const q = params.get("id");
    if (q) runLookup(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="lookup-page">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(242,106,33,0.10),transparent_55%)]" />
        <div className="relative max-w-[1300px] mx-auto px-5 sm:px-8 pt-14 md:pt-20 pb-6">
          <motion.p className="overline text-[#F26A21] mb-5" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            Track your plan
          </motion.p>
          <motion.h1
            className="font-heading font-bold text-5xl sm:text-6xl tracking-tight text-white leading-[1.05]"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            Customer{" "}
            <span className="text-[#F26A21]">lookup.</span>
          </motion.h1>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              runLookup(id);
            }}
            data-testid="lookup-form"
            className="mt-10 flex flex-col sm:flex-row gap-3 max-w-2xl"
          >
            <input
              data-testid="lookup-input"
              value={id}
              onChange={(e) => setId(e.target.value.toUpperCase())}
              placeholder="ENTER YOUR ID — e.g. ASA-123456"
              className="flex-1 h-14 bg-[#0C1C2E] border border-[#223851] rounded-xl px-5 font-mono text-white placeholder:text-[#6B7E92] focus:border-[#F26A21] focus:outline-none focus:ring-2 focus:ring-[#F26A21]/30 transition-all"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="lookup-submit"
              className="h-14 px-8 bg-[#F26A21] text-white rounded-full font-semibold flex items-center justify-center gap-2 hover:bg-[#DE5C15] transition-colors disabled:opacity-60"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : <>Look up <Search size={16} /></>}
            </button>
          </form>
        </div>
      </section>

      <section className="max-w-[1300px] mx-auto px-5 sm:px-8 py-14">
        {notFound && (
          <div data-testid="lookup-notfound" className="rounded-3xl border border-[#223851] bg-[#122539] p-12 text-center">
            <SearchX size={40} className="text-[#6B7E92] mx-auto" />
            <p className="font-heading font-bold text-2xl text-white mt-4">No record found</p>
            <p className="text-[#AEBECD] mt-2">Double-check your Customer ID, or register to get one.</p>
          </div>
        )}

        {customer && (
          <motion.div
            data-testid="lookup-dashboard"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[
                ["Customer", customer.name, customer.customer_id, true],
                ["Plan", customer.plan_name, customer.status, false],
                ["Next service", customer.next_service_date, customer.city, false],
                ["Member since", (customer.joined_at || "").slice(0, 10), "", false],
              ].map(([label, main, sub, mono], i) => (
                <div key={i} className="rounded-2xl bg-[#122539] border border-[#223851] p-5">
                  <p className="overline text-[#6B7E92]">{label}</p>
                  <p className="font-heading font-bold text-lg text-white mt-2">{main}</p>
                  {sub && (
                    <p className={`mt-1 text-sm ${mono ? "font-mono text-[#F26A21]" : "text-[#AEBECD]"}`}>{sub}</p>
                  )}
                </div>
              ))}
            </div>

            {customer.appliances?.length > 0 && (
              <div className="mt-8">
                <p className="overline text-[#6B7E92] mb-3">Covered appliances</p>
                <div className="flex flex-wrap gap-3">
                  {customer.appliances.map((a) => (
                    <span key={a} className="text-sm rounded-full border border-[#223851] bg-[#122539] px-4 py-2 text-[#D3DEE8]">
                      {a}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-8 rounded-2xl bg-[#122539] border border-[#223851] p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <p className="font-heading font-semibold text-white">How was your last service?</p>
                <p className="text-sm text-[#AEBECD]">Your feedback keeps our engineers sharp.</p>
              </div>
              <Link
                to={`/feedback?id=${customer.customer_id}`}
                data-testid="lookup-feedback-cta"
                className="shrink-0 inline-flex items-center gap-2 bg-[#F26A21] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#DE5C15] transition-colors"
              >
                Rate your service
              </Link>
            </div>

            <div className="mt-10">
              <p className="overline text-[#6B7E92] mb-4">Service history</p>
              <div className="rounded-2xl border border-[#223851] overflow-hidden" data-testid="service-history">
                {customer.visits?.map((v, i) => {
                  const Icon = v.status === "completed" ? CheckCircle2 : CalendarClock;
                  return (
                    <div
                      key={i}
                      className="grid grid-cols-1 md:grid-cols-12 gap-3 p-5 bg-[#122539] border-b border-[#223851] last:border-b-0"
                    >
                      <div className="md:col-span-2 font-mono text-white">{v.date}</div>
                      <div className="md:col-span-4 font-heading font-semibold text-white flex items-center gap-2">
                        <Icon size={16} className="text-[#F26A21]" />
                        {v.type}
                      </div>
                      <div className="md:col-span-4 text-sm text-[#AEBECD]">{v.note}</div>
                      <div className="md:col-span-2">
                        <span className={`text-xs font-semibold uppercase tracking-wide rounded-full border px-3 py-1 ${STATUS_STYLE[v.status] || STATUS_STYLE.upcoming}`}>
                          {v.status}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </section>
    </div>
  );
}
